import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { AuthDialog } from "@/components/AuthDialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  CalendarClock, Wind, MessageCircleHeart, LifeBuoy, ArrowRight,
  Cigarette, Brain, Hand, Sparkles, Unlock, Sunrise, Heart, RefreshCw, Compass, Target,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const GRACE_IMG = "https://images.unsplash.com/photo-1566231270035-0aaca1fd2bbf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const STEPS = [
  { icon: Brain, tag: "Understand", desc: "See what your brain is doing in a craving — the trigger, the loop, the automatic pull.", color: "text-brand-teal-dark", ring: "border-brand-teal/30" },
  { icon: Hand, tag: "Interrupt", desc: "Break the pattern in the moment with a practical alternative before the urge decides for you.", color: "text-brand-teal-dark", ring: "border-brand-teal/40" },
  { icon: Sparkles, tag: "Renew", desc: "Choose the next step and invite God in — every resisted urge strengthens a new response.", color: "text-brand-gold", ring: "border-brand-gold/40" },
];

const VALUES = [
  { icon: Unlock, label: "Freedom" },
  { icon: Sunrise, label: "Hope" },
  { icon: Heart, label: "Grace" },
  { icon: RefreshCw, label: "Renewal" },
  { icon: Compass, label: "Purpose" },
];

const FEATURES = [
  { icon: Hand, title: "Craving intervention", desc: "Before you log a slip, a guided moment helps you name the urge, try an alternative, and turn to God — Understand, Interrupt, Renew." },
  { icon: CalendarClock, title: "Personalized taper plans", desc: "A gentle, day-by-day step-down schedule to quit cigarettes, vaping, or chewing tobacco — anchored to your quit day." },
  { icon: Target, title: "Progress that proves it", desc: "Log every cigarette, watch your chain breaks add up, and see honest 7-day trends — progress, not perfection." },
  { icon: Wind, title: "The 72-hour toolkit", desc: "Guided breathing, urge timers, grounding, and prayer to carry you through the hardest first days." },
  { icon: MessageCircleHeart, title: "A faith-based companion", desc: "Someone to walk beside you with Scripture, encouragement, and practical tools — anytime, without judgment." },
  { icon: LifeBuoy, title: "Crisis support", desc: "One tap to helplines and grounding whenever things feel overwhelming. You are never doing this alone." },
];

const FAQS = [
  { q: "Is Not By Will really free?", a: "Yes. The heart of Not By Will — building your quit plan, honest logging, and the Break the Chain craving tool — is always free, with no card required. A few deeper features are offered to members, and sponsored access means cost is never the reason someone can't get help." },
  { q: "Is this a Christian, faith-based way to quit nicotine?", a: "Yes. Not By Will is God-centered. We pair Scripture, prayer, and biblical encouragement with the way He designed your brain to heal — helping you understand the urge, interrupt the pattern, and renew your mind." },
  { q: "Does it work for vaping and chewing tobacco, or just cigarettes?", a: "All of them. Not By Will supports cigarettes, vaping (tracked in puffs), and chewing tobacco (tracked in pouches). Your plan and progress adapt to whatever you're leaving behind." },
  { q: "What happens if I slip and use nicotine again?", a: "You log it honestly, with no shame, guilt, or talk of failure. A setback is information, not a verdict — we learn from it, adjust your plan, and keep moving forward in grace." },
  { q: "Is this medical treatment or counseling?", a: "No. Not By Will offers faith-based support and encouragement, not medical advice, diagnosis, or therapy. Nicotine withdrawal is uncomfortable but not usually dangerous; if you have a medical condition, talk with your doctor, and in an emergency call 911." },
  { q: "Do I need a Google account to join?", a: "No. You can create an account with just your email and a password, or continue with Google — whichever feels right. Signing up takes less than a minute and it's free." },
  { q: "Is my information private?", a: "Your recovery is personal, and we treat it that way. Your logs, cravings, and prayers are tied to your account and are never sold.", link: "/privacy", linkText: "Read our Privacy Policy →" },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const openAuth = (mode) => {
    if (user) {
      navigate("/app");
      return;
    }
    setAuthMode(mode);
    setAuthOpen(true);
  };

  return (
    <div className="relative bg-white text-brand-navy overflow-hidden min-h-screen">
      {/* Header */}
      <header className="relative z-20 sticky top-0 bg-white/90 backdrop-blur-xl border-b border-black/5">
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-2.5" data-testid="landing-brand">
            <Logo className="w-8 h-8" />
            <span className="font-heading font-extrabold tracking-[0.14em] text-lg">NOT BY WILL</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              data-testid="header-blog-link"
              to="/blog"
              className="hidden sm:inline-block text-sm font-medium text-stone hover:text-brand-navy transition-colors px-2"
            >
              Blog
            </Link>
            <button
              data-testid="header-login-btn"
              onClick={() => openAuth("signin")}
              className="rounded-full border border-black/10 px-5 py-2.5 text-sm font-medium transition-colors duration-200 hover:bg-sage"
            >
              Sign in
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 sm:px-10 max-w-7xl mx-auto pt-14 sm:pt-20 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-teal-dark mb-5">By His strength · And our faith</p>
            <h1 className="font-heading font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Freedom isn't found in trying harder.
            </h1>
            <p className="mt-6 text-lg text-stone leading-relaxed max-w-xl">
              Not By Will is a God-centered recovery companion built on faith <span className="text-brand-navy font-medium">and</span> the way
              He designed your brain to heal. Understand the urge. Interrupt the pattern. Renew the mind — one step at a time.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-get-started"
                onClick={() => openAuth("signup")}
                className="flex items-center justify-center gap-2 rounded-xl bg-brand-teal-dark px-7 py-4 text-white font-semibold transition-colors duration-200 hover:bg-[#0A6F66] shadow-glow-teal"
              >
                Get started free <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#how"
                className="flex items-center justify-center gap-2 rounded-xl border border-black/10 px-7 py-4 font-medium transition-colors duration-200 hover:bg-sage"
              >
                How it works <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm text-stone">
              <span className="flex items-center gap-1.5"><Cigarette className="w-4 h-4 text-brand-teal-dark" /> Quit nicotine</span>
              <span className="flex items-center gap-1.5"><Heart className="w-4 h-4 text-brand-teal-dark" /> Faith-centered</span>
              <span className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-brand-teal-dark" /> Judgment-free</span>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
          >
            <div className="relative rounded-[2rem] overflow-hidden border border-black/10 shadow-soft-lg">
              <img src={HERO_IMG} alt="Light over a calm mountain lake" className="w-full h-[440px] lg:h-[560px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-brand-navy/85 backdrop-blur-md border border-white/10 p-5">
                <p className="font-heading text-xl leading-relaxed text-white">
                  “Not by might, nor by power, but by my Spirit, says the LORD.”
                </p>
                <p className="text-xs text-brand-gold-light mt-2 tracking-wide">Zechariah 4:6</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Understand -> Interrupt -> Renew */}
      <section id="how" className="relative z-10 border-y border-black/5 bg-sage/50 py-20">
        <div className="px-6 sm:px-10 max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-teal-dark mb-4">How freedom is built</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">Understand. Interrupt. Renew.</h2>
            <p className="text-stone mt-3 leading-relaxed">
              This isn't willpower, and it isn't blind faith. It's His strength, our faith, and intentional action working together.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.tag}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`rounded-2xl bg-white border ${s.ring} p-7 shadow-soft`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-bold text-stone">0{i + 1}</span>
                    <div className={`rounded-xl bg-sage w-10 h-10 flex items-center justify-center ${s.color}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                  </div>
                  <h3 className={`font-heading text-2xl font-medium mb-2 ${s.color}`}>{s.tag}</h3>
                  <p className="text-stone leading-relaxed text-sm">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value words */}
      <section className="relative z-10 py-14">
        <div className="px-6 sm:px-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex flex-col items-center text-center gap-2"
                >
                  <Icon className="w-6 h-6 text-brand-teal-dark" strokeWidth={1.5} />
                  <span className="font-heading tracking-[0.18em] uppercase text-sm text-brand-navy">{v.label}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 py-20">
        <div className="px-6 sm:px-10 max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-teal-dark mb-4">Everything you need</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">Modern recovery tools, held together by grace.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="rounded-2xl bg-white border border-border p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand-teal/40"
                >
                  <div className="rounded-xl bg-sage w-12 h-12 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-teal-dark" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-heading text-xl font-medium mb-2 text-brand-navy">{f.title}</h3>
                  <p className="text-stone leading-relaxed text-sm">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Faith band */}
      <section className="relative z-10 py-20">
        <div className="px-6 sm:px-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-[2rem] overflow-hidden border border-black/10 shadow-soft-lg order-2 lg:order-1">
            <img src={GRACE_IMG} alt="Sunlight breaking through a forest, lighting a path forward" className="w-full h-[420px] object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-teal-dark mb-4">You were never meant to do this alone</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mb-6">
              Your brain can change. So can your story.
            </h2>
            <p className="text-lg text-stone leading-relaxed mb-4">
              God created the mind with a remarkable capacity to learn, adapt, and form new pathways. Not By Will helps you
              work with those God-given mechanisms — while continually pointing you to the One who gives you strength.
            </p>
            <p className="text-lg text-stone leading-relaxed">
              A setback isn't proof you lack faith. It's information — so we adjust the plan and keep moving forward, in grace.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 border-t border-black/5 bg-sage/40 py-20">
        <div className="px-6 sm:px-10 max-w-3xl mx-auto">
          <div className="mb-10">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-teal-dark mb-4">Questions &amp; grace</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">Common questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-black/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-heading text-lg text-brand-navy py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-stone leading-relaxed text-[15px]">
                  {f.a}
                  {f.link && (
                    <> <Link to={f.link} className="text-brand-teal-dark font-medium hover:underline">{f.linkText}</Link></>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-6 sm:px-10 pb-24">
        <div className="max-w-7xl mx-auto rounded-[2.5rem] bg-gradient-to-br from-brand-teal to-brand-teal-dark text-brand-ivory p-12 sm:p-20 text-center relative overflow-hidden">
          <h2 className="font-heading text-4xl sm:text-6xl font-semibold tracking-tight mb-6">You don't have to white-knuckle it.</h2>
          <p className="text-brand-ivory/80 text-lg max-w-xl mx-auto mb-9">
            Free to begin. Sign in and we'll build your personalized plan in minutes.
          </p>
          <button
            data-testid="footer-get-started"
            onClick={() => openAuth("signup")}
            className="inline-flex items-center gap-3 rounded-xl bg-brand-navy px-8 py-4 text-white font-semibold transition-transform duration-200 hover:scale-[1.02]"
          >
            Get started free <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />

      <footer className="relative z-10 border-t border-black/5 py-8 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto flex flex-col gap-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Logo className="w-6 h-6" />
              <span className="font-heading font-extrabold tracking-[0.14em] text-sm">NOT BY WILL</span>
            </div>
            <div className="flex items-center gap-5 text-sm">
              <Link data-testid="footer-blog-link" to="/blog" className="text-stone hover:text-brand-navy transition-colors">Blog</Link>
              <Link data-testid="footer-privacy-link" to="/privacy" className="text-stone hover:text-brand-navy transition-colors">Privacy Policy</Link>
              <Link data-testid="footer-terms-link" to="/terms" className="text-stone hover:text-brand-navy transition-colors">Terms of Use</Link>
            </div>
          </div>
          <p className="text-xs text-stone text-center sm:text-left max-w-2xl">
            Not By Will offers support and encouragement, not medical advice. Nicotine withdrawal is uncomfortable but
            not usually dangerous; if you have a medical condition, talk with your doctor. In an emergency, call 911.
          </p>
        </div>
      </footer>
    </div>
  );
}
